import {
  type ReactNode,
  type ReactElement,
  forwardRef,
  type ComponentPropsWithoutRef,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Help text variants using class-variance-authority
 */
const helpTextVariants = cva('text-muted-foreground', {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
    },
  },
  defaultVariants: {
    size: 'xs',
  },
});

export interface HelpTextProps
  extends ComponentPropsWithoutRef<'p'>, VariantProps<typeof helpTextVariants> {
  /**
   * The text size
   * @default 'xs'
   */
  size?: 'xs' | 'sm';

  /**
   * The semantic HTML element to render (p, span, div)
   * @default 'p'
   */
  as?: 'p' | 'span' | 'div';

  /**
   * The content to display
   */
  children: ReactNode;
}

/**
 * Base HelpText component with variant support
 */
const HelpTextBase = forwardRef<HTMLParagraphElement, HelpTextProps>(
  (
    { size = 'xs', as = 'p', children, className, ...props },
    ref
  ): ReactElement => {
    const Component = as;

    return (
      <Component
        ref={ref}
        className={cn(helpTextVariants({ size }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

HelpTextBase.displayName = 'HelpTextBase';

/**
 * HelpText - Small help text (text-xs text-muted-foreground)
 * @example
 * ```tsx
 * <HelpText>This field is optional</HelpText>
 * ```
 */
export const HelpText = forwardRef<
  HTMLParagraphElement,
  Omit<HelpTextProps, 'size'>
>(({ as = 'p', ...props }, ref) => (
  <HelpTextBase ref={ref} size="xs" as={as} {...props} />
));
HelpText.displayName = 'HelpText';

/**
 * Description - Description text (text-sm text-muted-foreground)
 * @example
 * ```tsx
 * <Description>This is a longer description of the field</Description>
 * ```
 */
export const Description = forwardRef<
  HTMLParagraphElement,
  Omit<HelpTextProps, 'size'>
>(({ as = 'p', ...props }, ref) => (
  <HelpTextBase ref={ref} size="sm" as={as} {...props} />
));
Description.displayName = 'Description';
